import { useCompanyId } from "@context-providers/company-id-provider";
import { grey, TeamIcon, UserCardItem, useToast } from "@flat-ui";
import { useState } from "react";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { useMembersUtils } from "@hooks/use-member-utils";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiConst, FaroCoreApiUtils } from "@stellar/core-api-client";
import { formatUserRoleType } from "@utils/data-display";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { getPendingUserIcon } from "@utils/user-utils";
import { isProjectRole } from "@custom-types/member-type-guards";
import { UtilsTypes } from "@stellar/utils";
/** Renders a project member using the UserCardItem component */
export function ProjectMember({
  member,
  shouldHideRemove,
  isRoleEditable,
  onSelectOption,
  projectId,
  location
}) {
  const {
    openToast
  } = useToast();
  const {
    openProfilePage,
    openViewTeamMembers
  } = useMembersUtils();
  const companyId = useCompanyId();
  const {
    changeMemberRole
  } = useChangeMemberRole({
    companyId,
    location,
    subjectType: "project",
    memberIdentity: member.identity,
    currentRole: member.role,
    selectedProject: {
      id: projectId
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(member.role);

  /** Is the member a team */
  const isMemberTeam = FaroCoreApiUtils.Member.isTeam(member);
  const roleDisplayName = UtilsTypes.RoleDisplayNames[member.role];

  /** The owner role cannot be reassigned to or away from */
  const isOwner = member.role === "RESOURCE_OWNER";
  const canEditRole = isRoleEditable && !isOwner;

  // When the role can be edited, offer all project roles (owner disabled), else
  // show only the current role as a read-only entry.
  const roleDropdownOptions = canEditRole ? FaroCoreApiConst.allProjectRoles.map(role => ({
    key: role,
    label: formatUserRoleType(role),
    value: role,
    isDisabled: role === "RESOURCE_OWNER"
  })) : [{
    key: member.role,
    label: formatUserRoleType(member.role),
    value: member.role
  }];

  // A dropdown holding only the current role and no remove entry offers nothing
  // to pick, so it is rendered as a disabled dropdown to stay aligned with the
  // editable rows while signalling it cannot be changed.
  const isRoleReadOnly = shouldHideRemove && !canEditRole;

  /**
   * Handles an option selected from the card dropdown: either forwards the
   * removal request or applies the selected role change.
   */
  async function onOptionSelected({
    target
  }) {
    // Prevent duplicate submissions while already loading
    if (isLoading) {
      return;
    }
    if (target.value === "remove") {
      onSelectOption({
        target
      }, member);
      return;
    }

    // Removal can be allowed while role editing is disabled; the dropdown then only holds the
    // current role, so ignore any role selection to avoid triggering a redundant role update.
    if (!canEditRole) {
      return;
    }
    if (!isProjectRole(target.value)) {
      return;
    }
    try {
      setIsLoading(true);

      // Update the selected option immediately for a responsive UI as happy path,
      // but revert to the original role if the API call fails.
      setSelectedOption(target.value);
      await changeMemberRole(target.value);
    } catch {
      // Errors are handled by the respective slices

      // Revert to the original role if it fails to update the role
      setSelectedOption(member.role);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Opens the profile page of the user or the team page members.
   * The permission validation is done in the canOpenProfilePage variable.
   */
  function openProfileOrTeamMembersClick(member_0) {
    if (!isMemberTeam) {
      openProfilePage({
        member: member_0,
        source: "user more info menu project member list"
      });
      return;
    }
    if (member_0.id) {
      openViewTeamMembers({
        teamId: member_0.id
      });
      return;
    }
    openToast({
      title: `${capitalizeFirstLetter(TEAM_DISPLAY_NAME)} profile page cannot be opened`,
      message: `The ${TEAM_DISPLAY_NAME} ID is not available. Please reload the page to try again.`,
      variant: "error"
    });
  }
  return <UserCardItem key={member.identity} shouldShowProfileCard={true} actionButtonLabel={isMemberTeam ? "View Members" : "Profile"} onProfileCardClick={() => openProfileOrTeamMembersClick(member)} shouldHideRemove={shouldHideRemove} icon={isMemberTeam ? <TeamIcon /> : getPendingUserIcon(member)} displayOption={FaroCoreApiUtils.Member.isMemberActive(member) ? "nameOnly" : "emailOnly"} avatarSx={isMemberTeam ? {
    bgcolor: grey[700]
  } : undefined} isLoading={isLoading} isDisabled={isRoleReadOnly} userInfo={{
    name: member.name,
    identity: member.identity,
    email: isMemberTeam ? "" : member.email,
    profileImageUrl: member.thumbnailUrl,
    userRole: roleDisplayName
  }} selectedOption={selectedOption} dropdownOptions={roleDropdownOptions} onChange={option => void onOptionSelected(option)} />;
}